import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { reverse } from "@reactivated";

import bestMatchIcon from "../../../../img/icons/mattress-match/first-match.svg";
import secondMatchIcon from "../../../../img/icons/mattress-match/second-match.svg";
import { addProductToBasket } from "../../../apps/configurator/actions";
import { ConfiguratorFinancing } from "../../../common/ConfiguratorFinancing";
import { ConfiguratorProductPrice } from "../../../common/ConfiguratorProductPrice";
import { Image } from "../../../common/Image";
import { Link } from "../../../common/Link";
import RichText from "../../../common/RichText";
import { strings } from "../../../localization";
import { IProduct } from "../../../models/catalogue.interfaces";
import { isoWebPageURL } from "../../../models/nominals";
import { trackAddToBasketEvent } from "../../../utils/analytics";
import urls from "../../../utils/urls";
import { DiscountDisplayMode } from "../../configurator/constants";
import { ValueProps } from "../components/ValueProps";

import styles from "./Result.module.scss";

interface IOwnProps {
    product: IProduct;
    productImg: string;
    productCopy: string;
    isBestMatch: boolean;
    feel: {
        icon_url: string | null;
        name: string;
    } | null;
}

type IProps = IOwnProps;

export const Result = (props: IProps) => {
    const onAddToBasket = async (
        event: React.MouseEvent<HTMLElement>,
        product: IProduct,
    ) => {
        event.preventDefault();
        try {
            await addProductToBasket(product.url);
            trackAddToBasketEvent(product);
            urls.navigateToURL(isoWebPageURL.wrap(reverse("basket:summary")));
        } catch (e) {
            return;
        }
    };

    const attrs = props.product?.attributes;

    return (
        <div className={styles.result}>
            <div className={styles.headerContainer}>
                <SVG
                    src={props.isBestMatch ? bestMatchIcon : secondMatchIcon}
                />
                <div className={styles.header}>
                    <h2 className={styles.productTitle}>
                        {props.product.parent?.title}
                    </h2>
                    <div className={styles.feelContainer}>
                        <div className={styles.iconContainer}>
                            {props.feel?.icon_url && (
                                <Image
                                    data-src={props.feel.icon_url}
                                    alt={props.feel.name}
                                />
                            )}
                        </div>
                        {/* TODO(i18n): Feel value needs proper context - consider using attribute display name or wrapping in translation if this is a user-facing label rather than raw data */}
                        <p className={styles.productFeel}>
                            {attrs.option_feel?.value}
                        </p>
                    </div>
                </div>
            </div>
            <div className={styles.productContainer}>
                <div className={styles.productInfo}>
                    <div className={styles.imageContainer}>
                        <Image
                            data-src={props.productImg}
                            className={styles.productImage}
                            alt=""
                            lazy={true}
                        />
                    </div>
                    <div className={styles.priceWrapper}>
                        <div className={styles.priceContent}>
                            <div>
                                <ConfiguratorProductPrice
                                    price={props.product?.price}
                                    strikeThroughMSRP={false}
                                    discountDisplayMode={
                                        DiscountDisplayMode.SHOW_ABSOLUTE
                                    }
                                    actualPriceStyle={"match"}
                                ></ConfiguratorProductPrice>
                                {/* TODO(i18n): Size value needs proper context - consider using attribute display name or wrapping in translation if this is a user-facing label rather than raw data */}
                                <span className={styles.sizeDisplay}>
                                    {
                                        props.product.attributes.option_size
                                            ?.value
                                    }
                                </span>
                            </div>
                            <ConfiguratorFinancing
                                price={props.product?.price}
                                variant={props.product}
                            ></ConfiguratorFinancing>
                        </div>
                    </div>
                    <Link
                        className={styles.detailsButton}
                        target="_top"
                        href={props.product.link}
                    >
                        {t`Product Details`}
                    </Link>
                    <button
                        className={styles.addButton}
                        onClick={(e) => {
                            onAddToBasket(e, props.product);
                        }}
                    >
                        {t`Add to Cart`}
                    </button>
                    {props.product.value_props && <ValueProps />}
                </div>
                <div className={styles.optionContainer}>
                    <h1>{strings.get("MATTRESS_SELECTOR_RESULTS_HEADING")}</h1>
                    <RichText html={props.productCopy} />
                </div>
            </div>
        </div>
    );
};
